import qs from 'qs'

import { Languages } from '../../constants/Languages'

/**
 * Strapi v5 populate descriptor. The legacy `populate=deep` plugin has been
 * dropped, so relations/components/media must be populated explicitly.
 * `'*'` covers one level (enough for flat relations & media); a nested object
 * is needed wherever a component/dynamic-zone contains its own relations.
 */
type Populate = '*' | Record<string, unknown>

class URI {
  private static build(path: string, lang?: Languages, populate: Populate = '*'): string {
    return (
      process.env.REACT_APP_API_URL +
      path +
      qs.stringify(
        {
          locale: lang,
          populate,
        },
        { addQueryPrefix: true, indices: false }
      )
    )
  }

  public static getDataUri(path: string, lang?: Languages, populate: Populate = '*'): string {
    return URI.build(path, lang, populate)
  }

  /** Estate lives in per-language collections (`/estate-de`, …); criteria is a
   *  repeatable component nested inside `profileCriteria`, and map `markers` nested
   *  inside `locationsMap`, so both need depth 2. */
  public static getEstateUri(lang?: Languages): string {
    return URI.build(`/estate-${lang}`, undefined, {
      gallery: true,
      locationsMap: { populate: { markers: true } },
      profileCriteria: { populate: { criteria: true } },
    })
  }

  /** `map` is a component with nested repeatable `markers`. */
  public static getContactUri(lang?: Languages): string {
    return URI.build('/wm-contact', lang, {
      address: true,
      map: { populate: { markers: true } },
    })
  }

  /** `map` is a component with nested repeatable `markers`. */
  public static getRentUri(lang?: Languages): string {
    return URI.build('/rent', lang, {
      gallery: true,
      localizations: true,
      map: { populate: { markers: true } },
    })
  }

  /** `sections` (dynamic zone) and `tenantService` both wrap nested components. */
  public static getTenantUri(lang?: Languages): string {
    return URI.build('/tenant', lang, {
      sections: { populate: '*' },
      tenantService: { populate: '*' },
    })
  }

  /** `navItems` carries nested `dropdownItems`. */
  public static getMenuUri(lang?: Languages): string {
    return URI.build('/menu', lang, {
      navItems: { populate: '*' },
    })
  }

  public static getPageUri(slug: string, lang?: Languages): string {
    return (
      process.env.REACT_APP_API_URL +
      '/wm-pages' +
      qs.stringify(
        {
          filters: {
            slug: {
              $eq: `${slug}_${lang}`,
            },
          },
          locale: lang,
          populate: '*',
        },
        { addQueryPrefix: true, indices: false }
      )
    )
  }

  public static getInvestmentsUri(lang?: Languages): string {
    return URI.build('/wm-invests', lang, { images: true })
  }

  public static getInvestmentUri(slug: string, lang?: Languages): string {
    return (
      process.env.REACT_APP_API_URL +
      '/wm-invests' +
      qs.stringify(
        {
          filters: {
            slug: {
              $eq: `${slug}`,
            },
          },
          locale: lang,
          populate: {
            images: true,
          },
        },
        { addQueryPrefix: true, indices: false }
      )
    )
  }

  public static getSponsorshipsUri(lang?: Languages): string {
    return URI.build(`/sponsoring-${lang}s`, lang, { image: true })
  }

  public static getSponsorshipUri(slug: string, lang?: Languages): string {
    return (
      process.env.REACT_APP_API_URL +
      `/sponsoring-${lang}s` +
      qs.stringify(
        {
          filters: {
            slug: {
              $eq: `${slug}`,
            },
          },
          locale: lang,
          populate: {
            image: true,
            elements: { populate: { images: true } },
          },
        },
        { addQueryPrefix: true, indices: false }
      )
    )
  }

  public static getPressUri(lang?: Languages): string {
    return URI.build(`/press-${lang}s`, lang, { image: true })
  }

  public static getPressSingleUri(slug: string, lang?: Languages): string {
    return (
      process.env.REACT_APP_API_URL +
      `/press-${lang}s` +
      qs.stringify(
        {
          filters: {
            slug: {
              $eq: `${slug}`,
            },
          },
          locale: lang,
          populate: {
            image: true,
            articles: { populate: { image: true } },
          },
        },
        { addQueryPrefix: true, indices: false }
      )
    )
  }
}

export default URI
